import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { supabase } from '../../../lib/supabase';

const PLATFORM_ROLES = [
  { value: 'user', label: 'Standard User', description: 'Regular access based on org permissions', icon: 'User', color: 'text-gray-600 bg-gray-100' },
  { value: 'superadmin', label: 'Super Admin', description: 'Full platform access across all organizations', icon: 'Shield', color: 'text-purple-600 bg-purple-100' },
];

const ORG_ROLES = [
  { value: 'owner', label: 'Owner', description: 'Full org control, billing access', icon: 'Crown', color: 'text-yellow-600 bg-yellow-100' },
  { value: 'admin', label: 'Admin', description: 'Manage org settings and users', icon: 'ShieldCheck', color: 'text-blue-600 bg-blue-100' },
  { value: 'manager', label: 'Manager', description: 'Manage operations and personnel', icon: 'Briefcase', color: 'text-green-600 bg-green-100' },
  { value: 'technician', label: 'Technician', description: 'Field work and ticket management', icon: 'Wrench', color: 'text-orange-600 bg-orange-100' },
  { value: 'sales', label: 'Sales', description: 'Quotes, customers, and sales pipeline', icon: 'TrendingUp', color: 'text-pink-600 bg-pink-100' },
  { value: 'viewer', label: 'Viewer', description: 'Read-only access to assigned areas', icon: 'Eye', color: 'text-gray-600 bg-gray-100' },
];

const RoleAssignmentModal = ({ person, onClose, onSaved }) => {
  const [platformRole, setPlatformRole] = useState('user');
  const [orgRole, setOrgRole] = useState('technician');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [userProfileId, setUserProfileId] = useState(null);
  const [orgMemberId, setOrgMemberId] = useState(null);
  const [orgId, setOrgId] = useState(null);

  useEffect(() => {
    loadCurrentRoles();
  }, [person]);

  const loadCurrentRoles = async () => {
    setLoading(true);
    setError('');
    try {
      // Find user profile by email or auth_user_id
      let profileQuery = supabase?.from('user_profiles')?.select('id, platform_role, email');
      if (person?.auth_user_id) {
        profileQuery = profileQuery?.eq('id', person?.auth_user_id);
      } else if (person?.email) {
        profileQuery = profileQuery?.eq('email', person?.email);
      }
      const { data: profile } = await profileQuery?.single();

      if (profile) {
        setUserProfileId(profile?.id);
        setPlatformRole(profile?.platform_role || 'user');

        // Load org membership
        const { data: membership } = await supabase
          ?.from('org_members')
          ?.select('id, role, organization_id')
          ?.eq('user_id', profile?.id)
          ?.eq('is_active', true)
          ?.single();

        if (membership) {
          setOrgMemberId(membership?.id);
          setOrgRole(membership?.role || 'technician');
          setOrgId(membership?.organization_id);
        } else {
          // Get first org
          const { data: org } = await supabase?.from('organizations')?.select('id')?.limit(1)?.single();
          if (org) setOrgId(org?.id);
        }
      } else {
        // No profile yet — get org for later creation
        const { data: org } = await supabase?.from('organizations')?.select('id')?.limit(1)?.single();
        if (org) setOrgId(org?.id);
      }
    } catch (err) {
      console.error('Error loading roles:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      if (userProfileId) {
        // Update platform role
        const { error: profileErr } = await supabase
          ?.from('user_profiles')
          ?.update({ platform_role: platformRole, updated_at: new Date()?.toISOString() })
          ?.eq('id', userProfileId);
        if (profileErr) throw profileErr;

        // Update or insert org membership
        if (orgMemberId) {
          const { error: memberErr } = await supabase
            ?.from('org_members')
            ?.update({ role: orgRole, updated_at: new Date()?.toISOString() })
            ?.eq('id', orgMemberId);
          if (memberErr) throw memberErr;
        } else if (orgId) {
          const { error: insertErr } = await supabase
            ?.from('org_members')
            ?.insert({ user_id: userProfileId, organization_id: orgId, role: orgRole, is_active: true });
          if (insertErr) throw insertErr;
        }

        // Log the role change
        await supabase?.from('audit_logs')?.insert({
          action: 'UPDATE',
          resource_type: 'user_role',
          resource_name: person?.name || person?.email,
          severity: 'info',
          status: 'success',
          user_email: 'admin',
          details: { platform_role: platformRole, org_role: orgRole, target_user: person?.email }
        });

        setSuccess('Roles updated successfully!');
        setTimeout(() => { onSaved?.(); onClose(); }, 1200);
      } else {
        setError('No linked user account found. The person must accept a login invite first.');
      }
    } catch (err) {
      setError(err?.message || 'Failed to update roles.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[2000] p-4">
      <div className="bg-card rounded-2xl shadow-elevation-3 w-full max-w-lg">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-border">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-purple-100 rounded-xl flex items-center justify-center">
              <Icon name="ShieldCheck" size={20} color="#7c3aed" />
            </div>
            <div>
              <h2 className="text-lg font-heading font-semibold text-foreground">Assign Role</h2>
              <p className="text-sm text-muted-foreground">{person?.name}</p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-muted transition-smooth">
            <Icon name="X" size={20} color="var(--color-muted-foreground)" />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : (
          <div className="p-6 space-y-6">
            {/* Platform Role */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-3">Platform Role</label>
              <div className="space-y-2">
                {PLATFORM_ROLES?.map(role => (
                  <button
                    key={role?.value}
                    onClick={() => setPlatformRole(role?.value)}
                    className={`w-full flex items-center gap-3 p-3 rounded-xl border-2 transition-all text-left ${
                      platformRole === role?.value
                        ? 'border-primary bg-primary/5' :'border-border hover:border-primary/40 hover:bg-muted/50'
                    }`}
                  >
                    <div className={`w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 ${role?.color}`}>
                      <Icon name={role?.icon} size={18} color="currentColor" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-foreground">{role?.label}</p>
                      <p className="text-xs text-muted-foreground">{role?.description}</p>
                    </div>
                    {platformRole === role?.value && (
                      <Icon name="CheckCircle" size={18} color="var(--color-primary)" />
                    )}
                  </button>
                ))}
              </div>
            </div>

            {/* Org Role */}
            <div>
              <label className="block text-sm font-semibold text-foreground mb-3">Organization Role</label>
              <div className="grid grid-cols-2 gap-2">
                {ORG_ROLES?.map(role => (
                  <button
                    key={role?.value}
                    onClick={() => setOrgRole(role?.value)}
                    className={`flex items-center gap-2 p-3 rounded-xl border-2 transition-all text-left ${
                      orgRole === role?.value
                        ? 'border-primary bg-primary/5' :'border-border hover:border-primary/40 hover:bg-muted/50'
                    }`}
                  >
                    <div className={`w-7 h-7 rounded-lg flex items-center justify-center flex-shrink-0 ${role?.color}`}>
                      <Icon name={role?.icon} size={14} color="currentColor" />
                    </div>
                    <div className="min-w-0">
                      <p className="text-xs font-medium text-foreground">{role?.label}</p>
                      <p className="text-xs text-muted-foreground truncate">{role?.description}</p>
                    </div>
                  </button>
                ))}
              </div>
            </div>

            {!userProfileId && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-3 flex items-start gap-2">
                <Icon name="AlertTriangle" size={16} color="#d97706" />
                <p className="text-sm text-yellow-700">No linked account found. Send a login invite first to enable role assignment.</p>
              </div>
            )}

            {error && (
              <div className="bg-red-50 border border-red-200 rounded-xl p-3 flex items-center gap-2">
                <Icon name="AlertCircle" size={16} color="#dc2626" />
                <p className="text-sm text-red-700">{error}</p>
              </div>
            )}
            {success && (
              <div className="bg-green-50 border border-green-200 rounded-xl p-3 flex items-center gap-2">
                <Icon name="CheckCircle" size={16} color="#16a34a" />
                <p className="text-sm text-green-700">{success}</p>
              </div>
            )}
          </div>
        )}

        <div className="flex items-center justify-end gap-3 p-6 border-t border-border">
          <button onClick={onClose} className="px-4 py-2 text-sm font-medium text-foreground border border-border rounded-lg hover:bg-muted transition-smooth">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || loading || !userProfileId}
            className="px-5 py-2 text-sm font-medium bg-primary text-primary-foreground rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</> : <><Icon name="Save" size={15} color="white" /> Save Roles</>}
          </button>
        </div>
      </div>
    </div>
  );
};

export default RoleAssignmentModal;
